class OnyxTimer extends HTMLElement {
  constructor() {
    super();
    this.totalTimeStr = this.dataset.totalTime || '03:10';
    this.totalSeconds = this.parseTime(this.totalTimeStr);
    this.circumference = 1570.8;
    this.currentTime = 0;
    this.currentWeight = 0;
    this.timerId = null;
    this.isRunning = false;
  }
  
  connectedCallback() {
    this.circleProgress = this.querySelector('.onyx-timer__circle-progress');
    this.displayTime = this.querySelector('.onyx-timer__display-time');
    this.weightDisplay = this.querySelector('.onyx-timer__current-weight');
    this.readyView = this.querySelector('.onyx-timer__ready-view');
    this.activeView = this.querySelector('.onyx-timer__active-view');
    
    this.startBtn = this.querySelector('.onyx-timer__start-btn');
    this.resumeBtn = this.querySelector('.onyx-timer__resume-btn');
    this.resetBtn = this.querySelector('.onyx-timer__reset-btn');
    
    this.steps = Array.from(this.querySelectorAll('.onyx-timer__step')).map((el, index, all) => {
      const time = this.parseTime(el.dataset.timestamp);
      const nextEl = all[index + 1];
      const nextTime = nextEl ? this.parseTime(nextEl.dataset.timestamp) : time;
      
      return {
        el,
        seconds: time,
        duration: nextTime - time,
        targetWeight: parseFloat(el.dataset.weight) || 0,
        flowRate: parseFloat(el.dataset.flowRate) || 0
      };
    });
    
    this.init();
  }
  
  disconnectedCallback() {
    this.cleanup();
  }
  
  cleanup() {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }
  
  init() {
    if (this.startBtn) this.startBtn.addEventListener('click', () => this.start());
    if (this.resumeBtn) this.resumeBtn.addEventListener('click', () => this.toggle());
    if (this.resetBtn) this.resetBtn.addEventListener('click', () => this.reset());
    
    this.updateProgress(0);
    this.highlightSteps(0);
  }
  
  parseTime(str) {
    if (!str) return 0;
    const parts = str.split(':');
    if (parts.length === 1) return parseInt(parts[0]);
    return parseInt(parts[0]) * 60 + parseInt(parts[1]);
  }
  
  formatTime(seconds) {
    const s = Math.floor(seconds);
    const m = Math.floor(s / 60);
    const ss = s % 60;
    return `${m.toString().padStart(1, '0')}:${ss.toString().padStart(2, '0')}`;
  }
  
  start() {
    if (this.readyView) this.readyView.style.display = 'none';
    if (this.activeView) this.activeView.style.display = 'block';
    if (this.resumeBtn) this.resumeBtn.style.display = 'block';
    if (this.resetBtn) this.resetBtn.style.display = 'block';
    if (this.resumeBtn) this.resumeBtn.textContent = 'PAUSE TIMER';
    this.isRunning = true;
    this.tick();
  }
  
  toggle() {
    if (this.isRunning) {
      this.pause();
    } else {
      this.resume();
    }
  }
  
  pause() {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
    this.isRunning = false;
    if (this.resumeBtn) this.resumeBtn.textContent = 'RESUME TIMER';
  }
  
  resume() {
    if (this.isRunning) return;
    this.isRunning = true;
    if (this.resumeBtn) this.resumeBtn.textContent = 'PAUSE TIMER';
    this.tick();
  }
  
  reset() {
    this.pause();
    this.currentTime = 0;
    this.currentWeight = 0;
    this.updateDisplay();
    this.updateProgress(0);
    this.highlightSteps(0);
    if (this.readyView) this.readyView.style.display = 'block';
    if (this.activeView) this.activeView.style.display = 'none';
    if (this.resumeBtn) {
      this.resumeBtn.style.display = 'none';
      this.resumeBtn.textContent = 'RESUME TIMER';
      this.resumeBtn.disabled = false;
    }
    if (this.resetBtn) this.resetBtn.style.display = 'none';
  }
  
  tick() {
    this.startTime = Date.now() - (this.currentTime * 1000);
    if (this.timerId) {
      clearInterval(this.timerId);
    }
    this.timerId = setInterval(() => {
      this.currentTime = (Date.now() - this.startTime) / 1000;
      this.updateDisplay();
      this.updateProgress(this.currentTime);
      this.updateWeight();
      this.highlightSteps(this.currentTime);
      
      if (this.currentTime >= this.totalSeconds) {
        this.pause();
        if (this.resumeBtn) {
          this.resumeBtn.textContent = 'FINISHED';
          this.resumeBtn.disabled = true;
        }
      }
    }, 10);
  }
  
  updateDisplay() {
    if (this.displayTime) this.displayTime.textContent = this.formatTime(this.currentTime);
  }
  
  updateWeight() {
    let weight = 0;
    this.steps.forEach(step => {
      if (this.currentTime >= step.seconds) {
        const elapsedInStep = Math.min(this.currentTime - step.seconds, step.duration);
        const weightToAdd = Math.max(0, step.targetWeight - weight);
        const weightInStep = Math.min(weightToAdd, elapsedInStep * step.flowRate);
        weight += weightInStep;
      }
    });
    this.currentWeight = weight;
    if (this.weightDisplay) this.weightDisplay.textContent = Math.floor(this.currentWeight);
  }
  
  updateProgress(seconds) {
    if (!this.circleProgress) return;
    const progress = Math.min(seconds / this.totalSeconds, 1);
    const offset = this.circumference * (1 - progress);
    this.circleProgress.style.strokeDashoffset = offset;
  }
  
  highlightSteps(seconds) {
    if (!this.steps) return;
    this.steps.forEach((step) => {
      if (seconds >= step.seconds) {
        step.el.classList.add('is-active');
      } else {
        step.el.classList.remove('is-active');
      }
    });
  }
}

if (!customElements.get('onyx-timer')) {
  customElements.define('onyx-timer', OnyxTimer);
}
